// LOOP CRM SEARCH IMPORT REDESIGN V24 — preview shows duplicate risk and import returns created/updated counts.
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { importCustomersForBusiness, normalizeEmail, normalizePhone } from "../utils/customerCrm";

import { importFields, readImportWorkbook, suggestMapping, prepareImport } from '../utils/customerImportMapping';

const BRAND = "#00675f";


function normalizeImportName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function importPreviewIdentity(customer = {}) {
  const phone = normalizePhone(customer.phone || customer.phoneNormalized || "");
  if (phone) return `phone:${phone}`;
  const email = normalizeEmail(customer.email || customer.emailNormalized || "");
  if (email) return `email:${email}`;
  const name = normalizeImportName(customer.name || customer.customerName || customer.fullName || "");
  return name && name.length >= 3 ? `name:${name}` : "";
}

function formatPct(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export default function CustomerImportModal({ open, businessId, onClose = () => {}, onDone = () => {} }) {
  const [fileName, setFileName] = useState("");
  const [sheets, setSheets] = useState([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [mapping, setMapping] = useState({});
  const [source, setSource] = useState('AgendaPro');
  const prepared = useMemo(() => prepareImport(sheets[sheetIndex]?.rows || [], mapping, source), [sheets, sheetIndex, mapping, source]);
  const customers = prepared.customers;
  const chooseSheet = index => { setSheetIndex(index); setMapping(suggestMapping(sheets[index]?.rows[0] || [])); };
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [review, setReview] = useState(null);
  useEffect(() => { setReview(null); setResult(null); }, [sheets, sheetIndex, mapping, source]);

  const preview = useMemo(() => customers.slice(0, 8), [customers]);
  const importSummary = useMemo(() => {
    const identityCounts = new Map();
    customers.forEach((customer) => {
      const key = importPreviewIdentity(customer);
      if (!key) return;
      identityCounts.set(key, (identityCounts.get(key) || 0) + 1);
    });
    const duplicateRows = Array.from(identityCounts.values()).reduce((sum, count) => sum + Math.max(0, count - 1), 0);
    const uniqueContacts = customers.length - duplicateRows;
    return { duplicateRows, uniqueContacts };
  }, [customers]);
  const canImport = !!businessId && customers.length > 0 && !prepared.errors.length && !importing && !parsing;

  if (!open) return null;

  async function handleFileChange(event) {
    const file = event.target?.files?.[0];
    setError("");
    setResult(null);
    setProgress(0);
    setStatusText("");
    setSheets([]);

    if (!file) return;

    setFileName(file.name || "Archivo seleccionado");
    setParsing(true);

    try {
      const parsed = await readImportWorkbook(file);
      setSheets(parsed); setSheetIndex(0); setMapping(suggestMapping(parsed[0]?.rows[0] || []));
      setStatusText('Revisa las columnas y la vista previa antes de importar.');
    } catch (err) {
      console.error("[CustomerImportModal] parse failed", err);
      setError(err?.message || "No se pudo leer el archivo.");
    } finally {
      setParsing(false);
      try { event.target.value = null; } catch (_) {}
    }
  }

  async function handleImport() {
    if (!businessId) {
      setError("No encuentro el businessId. Abre el importador desde la pantalla de edición de un negocio ya creado.");
      return;
    }

    if (!customers.length) {
      setError("Primero selecciona un archivo con clientes.");
      return;
    }

    setImporting(true);
    setError("");
    setResult(null);
    setProgress(0);
    setStatusText(`Importando 0 de ${customers.length} clientes...`);

    try {
      const importResult = await importCustomersForBusiness(db, businessId, customers, {
        chunkSize: 400,
        strictContactMatch: true,
        dryRun: !review,
        onProgress: ({ imported, total, percent }) => {
          setProgress(formatPct(percent));
          setStatusText(`Importando ${imported} de ${total} clientes...`);
        },
      });

      if (!review) { setReview(importResult); setProgress(0); setStatusText("Revisión terminada. Todavía no se han guardado cambios."); return; }
      setProgress(100);
      setStatusText("Importación completada.");
      setResult(importResult);
      onDone(importResult);
    } catch (err) {
      console.error("[CustomerImportModal] import failed", err);
      const msg = err?.code === "permission-denied"
        ? "Firestore rechazó la importación. Revisa reglas de businesses/{businessId}/customers."
        : err?.message || "No se pudo importar. Revisa la consola.";
      setError(msg);
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="ci-backdrop" onClick={importing ? undefined : onClose}>
      <style>{styles}</style>
      <section className="ci-modal" onClick={(e) => e.stopPropagation()}>
        <header className="ci-head">
          <div>
            <span>Clientes</span>
            <h2>Importar Excel / CSV</h2>
            <p>1. Sube el archivo · 2. Relaciona columnas · 3. Revisa e importa</p>
          </div>
          <button type="button" onClick={onClose} disabled={importing}>✕</button>
        </header>

        {!businessId ? (
          <div className="ci-alert is-error">
            No recibí <strong>businessId</strong>. Esto se corrige pasando <code>businessId={`{businessId}`}</code> al GeneralSection desde BusinessEditPage.
          </div>
        ) : null}

        <label className="ci-upload">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} disabled={parsing || importing} />
          <strong>{fileName || "Seleccionar archivo"}</strong>
          <span>Formatos: .xlsx, .xls o .csv</span>
        </label>

        {!!sheets.length && <section className="ci-mapping">
          <h3>Relaciona las columnas</h3>
          <p>Los campos adicionales se conservarán en la ficha como información importada.</p>
          <label>Origen<input value={source} onChange={e=>setSource(e.target.value)} disabled={importing}/></label>
          <label>Hoja<select value={sheetIndex} onChange={e=>chooseSheet(Number(e.target.value))} disabled={importing}>{sheets.map((sheet,i)=><option key={i} value={i}>{sheet.name}</option>)}</select></label>
          <div className="ci-map-grid">{importFields.map(([key,label])=><label key={key}>{label}<select value={mapping[key]??''} disabled={importing} onChange={e=>{const next={...mapping};if(e.target.value==='')delete next[key];else next[key]=e.target.value;setMapping(next);}}><option value="">Sin asignar</option>{(sheets[sheetIndex]?.rows[0]||[]).map((header,i)=><option key={i} value={i}>{header||'Columna '+(i+1)}</option>)}</select></label>)}</div>
          {!!prepared.errors.length && <div role="alert" className="ci-alert is-error"><strong>{prepared.errors.length} filas requieren revisión. No se importará hasta corregirlas.</strong>{prepared.errors.slice(0,8).map(item=><p key={item.row}>Fila {item.row}: {item.message}</p>)}</div>}
          <p>Esta carga importa contactos, no crea citas ni cobros. No activa permisos de comunicaciones.</p>
        </section>}
        {statusText ? <div className="ci-status">{statusText}</div> : null}
        {error ? <div className="ci-alert is-error">{error}</div> : null}
        {result ? <div className="ci-alert is-success">Importación lista: {result.created || 0} nuevos, {result.updated || 0} actualizados, {result.deduped || 0} duplicados del archivo combinados. Conflictos sin importar: {result.skippedConflicts || 0}. {result.batchId && "Puedes consultar el resultado o revertir este lote en Gestión del CRM → Importaciones."}</div> : null}

        {(importing || progress > 0) ? (
          <div className="ci-progress-wrap">
            <div className="ci-progress-meta"><span>Progreso</span><strong>{formatPct(progress)}%</strong></div>
            <div className="ci-progress"><span style={{ width: `${formatPct(progress)}%` }} /></div>
          </div>
        ) : null}

        {review && <div className="ci-dedupe-summary" role="status"><strong>Resultado de la revisión</strong><span>{review.created} nuevos · {review.updated} existentes para actualizar · {review.skippedConflicts} conflictos que se omitirán.</span><span>Se conservan los datos de contacto existentes; se agregan notas y etiquetas. Los campos originales quedan en Datos importados.</span></div>}
        {customers.length ? (
          <div className="ci-dedupe-summary">
            <strong>{importSummary.uniqueContacts} contacto{importSummary.uniqueContacts === 1 ? "" : "s"} único{importSummary.uniqueContacts === 1 ? "" : "s"}</strong>
            <span>{importSummary.duplicateRows > 0 ? `${importSummary.duplicateRows} fila${importSummary.duplicateRows === 1 ? "" : "s"} repetida${importSummary.duplicateRows === 1 ? "" : "s"} se combinarán antes de guardar.` : "No veo duplicados claros dentro del archivo."}</span>
          </div>
        ) : null}

        {preview.length ? (
          <div className="ci-preview">
            <div className="ci-preview-head">
              <strong>Vista previa</strong>
              <span>{customers.length} clientes detectados</span>
            </div>
            <div className="ci-table">
              <table>
                <thead>
                  <tr><th>Nombre</th><th>Teléfono</th><th>Email</th><th>Reservas</th><th>Última reserva</th></tr>
                </thead>
                <tbody>
                  {preview.map((c, idx) => (
                    <tr key={`${c.phone || c.email || c.name}-${idx}`}>
                      <td>{c.name || "—"}</td>
                      <td>{c.phone || "—"}</td>
                      <td>{c.email || "—"}</td>
                      <td>{c.importedTotalBookings || 0}</td>
                      <td>{c.importedLastBookingAtText || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <footer className="ci-actions">
          <button type="button" className="ci-secondary" onClick={onClose} disabled={importing}>Cerrar</button>
          <button type="button" className="ci-primary" onClick={handleImport} disabled={!canImport || !!result}>
            {importing ? "Procesando..." : review ? "Confirmar importación" : "Revisar coincidencias"}
          </button>
        </footer>
      </section>
    </div>
  );
}

const styles = `
.ci-mapping{margin-top:20px}.ci-mapping h3{font-size:18px}.ci-mapping p{font-size:13px;line-height:1.5}.ci-mapping label{display:grid;gap:6px;font-size:13px;margin:10px 0}.ci-mapping select,.ci-mapping input{width:100%;min-width:0;box-sizing:border-box;padding:10px;border:1px solid #cfddd9;border-radius:8px;background:white}.ci-map-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:0 14px}

.ci-backdrop{position:fixed;inset:0;z-index:9999;background:rgba(15,23,42,.42);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;padding:18px;box-sizing:border-box}.ci-modal{width:min(100%,920px);max-height:min(92vh,820px);overflow:auto;background:#fff;border:1px solid rgba(226,232,240,.95);border-radius:32px;box-shadow:0 30px 90px rgba(15,23,42,.28);padding:20px;color:#0f172a;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.ci-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:16px}.ci-head span{color:${BRAND};font-size:11px;font-weight:950;letter-spacing:.14em;text-transform:uppercase}.ci-head h2{margin:6px 0 0;font-size:34px;line-height:.95;letter-spacing:-.055em;font-weight:950}.ci-head p{margin:9px 0 0;color:#64748b;font-size:14px;font-weight:750}.ci-head button{width:38px;height:38px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#64748b;font-weight:950;cursor:pointer}.ci-upload{display:grid;place-items:center;text-align:center;border:1px dashed rgba(0,103,95,.36);background:linear-gradient(135deg,rgba(0,103,95,.06),#f8fafc);border-radius:26px;padding:28px;cursor:pointer}.ci-upload input{display:none}.ci-upload strong{color:${BRAND};font-size:17px;font-weight:950}.ci-upload span{margin-top:6px;color:#64748b;font-size:13px;font-weight:750}.ci-dedupe-summary{margin-top:12px;border:1px solid rgba(0,103,95,.16);background:#f0fdfa;color:#00675f;border-radius:18px;padding:12px 14px;display:grid;gap:4px}.ci-dedupe-summary strong{font-size:13px;font-weight:950}.ci-dedupe-summary span{font-size:12px;font-weight:850;line-height:1.35}.ci-status{margin-top:12px;border:1px solid #e2e8f0;background:#f8fafc;border-radius:18px;padding:12px 14px;color:#334155;font-size:13px;font-weight:850}.ci-alert{margin-top:12px;border-radius:18px;padding:12px 14px;font-size:13px;font-weight:850}.ci-alert.is-error{border:1px solid #fecaca;background:#fef2f2;color:#b91c1c}.ci-alert.is-success{border:1px solid #bbf7d0;background:#ecfdf5;color:#047857}.ci-alert code{background:rgba(15,23,42,.08);border-radius:8px;padding:2px 5px}.ci-progress-wrap{margin-top:14px}.ci-progress-meta{display:flex;align-items:center;justify-content:space-between;margin-bottom:7px;color:#64748b;font-size:12px;font-weight:900}.ci-progress-meta strong{color:${BRAND}}.ci-progress{height:10px;background:#e2e8f0;border-radius:999px;overflow:hidden}.ci-progress span{display:block;height:100%;background:${BRAND};border-radius:inherit;transition:width .18s ease}.ci-preview{margin-top:16px;border:1px solid #e2e8f0;border-radius:24px;overflow:hidden}.ci-preview-head{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:13px 14px;background:#f8fafc}.ci-preview-head strong{font-weight:950}.ci-preview-head span{color:#64748b;font-size:12px;font-weight:850}.ci-table{overflow:auto}.ci-table table{width:100%;border-collapse:collapse;min-width:720px}.ci-table th,.ci-table td{padding:11px 13px;border-top:1px solid #e2e8f0;text-align:left;font-size:13px}.ci-table th{color:#64748b;font-weight:950;background:#fff}.ci-table td{color:#0f172a;font-weight:750}.ci-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:16px}.ci-primary,.ci-secondary{min-height:48px;border-radius:16px;padding:0 16px;font-size:14px;font-weight:950;cursor:pointer}.ci-primary{border:0;background:${BRAND};color:#fff;box-shadow:0 14px 30px rgba(0,103,95,.22)}.ci-secondary{border:1px solid #e2e8f0;background:#fff;color:#475569}.ci-primary:disabled,.ci-secondary:disabled{opacity:.6;cursor:not-allowed}@media(max-width:640px){.ci-backdrop{align-items:flex-end;padding:10px}.ci-modal{border-radius:28px;padding:16px;max-height:92vh}.ci-head h2{font-size:28px}.ci-actions{position:sticky;bottom:0;background:linear-gradient(180deg,rgba(255,255,255,.72),#fff 30%);padding-top:12px}.ci-primary,.ci-secondary{flex:1}}
`;
